import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../components/common/PageLayout';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;

  return (
    <>
      <Helmet>
        <title>Privacy Policy | MWQQ.com - Privacy-First Passport Photos</title>
        <meta name="description" content="Read our privacy policy. We process all passport photos locally in your browser. No uploads, no storage, GDPR compliant. Your photos never leave your device." />
        <meta name="keywords" content="privacy policy, GDPR, data protection, passport photo privacy, local processing, no upload, secure photo tool" />
        <link rel="canonical" href={`${baseUrl}/privacy.html`} />
        <meta property="og:title" content="Privacy Policy | MWQQ.com" />
        <meta property="og:description" content="Privacy-first passport photo tool. All processing happens locally in your browser." />
        <meta property="og:url" content={`${baseUrl}/privacy.html`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageLayout title={t('pages.privacy.title')}>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('pages.privacy.lastUpdated')}: October 1, 2025
      </p>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.intro.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.intro.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.dataCollection.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.dataCollection.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.dataCollection.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.cookies.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.cookies.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.cookies.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.analytics.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.analytics.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.analytics.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.dataRetention.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.dataRetention.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.dataRetention.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.yourRights.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.yourRights.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.yourRights.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.security.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.security.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.security.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.changes.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.privacy.sections.changes.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.privacy.sections.contact.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.privacy.sections.contact.content')}
        </p>
        <a
          href="mailto:mwqq.com@gmail.com"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          mwqq.com@gmail.com
        </a>
      </section>

      <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Related:{' '}
          <Link to="/terms.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.terms')}
          </Link>
          {' | '}
          <Link to="/cookies.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.cookies')}
          </Link>
        </p>
      </div>
      </PageLayout>
    </>
  );
};

export default PrivacyPolicy;
